import { useEffect, useRef, useState } from 'react'
import { messageOf } from '../lib/errors'
import type { Me } from '../lib/me'
import { can, canGallery } from '../lib/me'
import { canReachShopAdmin } from '../lib/shop'
import { supabase } from '../lib/supabase'
import { myLanes } from '../lib/support'
import { AppShell } from '../components/AppShell'
import { Card } from '../components/Card'
import {
  AskOneChart,
  AskOneDetail,
  AskOneList,
  AskOneNext,
  AskOneThemes,
  AskOneTiles,
  SupportTabs,
} from '../components/AskOneDesk'
import {
  draftWikiAnswerFromAsk,
  loadAskOneCounts,
  loadAskOneQuestions,
  loadAskOneSummary,
  loadAskOneThemes,
  reopenAskOne,
  reviewAskOne,
  sendAskOneToDevelopers,
  type AskOneDays,
  type AskOneFilter,
  type AskOneQuestion,
  type AskOneSummary,
  type AskOneTheme,
} from '../lib/askOneDesk'

/**
 * `#/support/ask-one` — Ask One on the desk (126, HLP-35, David 29 Sep).
 *
 * What people asked One, how it answered, and four things to do about it:
 * review or dismiss, draft the missing Wiki One answer, send a fault to the
 * developers, and read the themes the 07:00 run writes. Design and the
 * decisions behind it: `ask-one-dashboard.html`.
 *
 * ── A SECTION OF #/support, NOT A NEW PLACE ─────────────────────────────────
 *
 * It sits beside the desk because the questions One could not answer are the
 * tickets that did not get written. A lane holder does not see it (the tab is
 * for analytics.read), which is 085's "a lane sees its own lane" holding.
 *
 * ── THE REFUSAL IS RENDERED, NOT SWALLOWED (rule 12) ────────────────────────
 *
 * Every read raises for somebody without analytics.read. The `can()` test
 * below is presentation on top of that; the sentence it renders is the same
 * one the server would have given.
 *
 * ── THE LOADS ARE LATEST-WINS ───────────────────────────────────────────────
 *
 * Filter, theme, search and period all re-query. A slow response for the old
 * filter landing after a fast one for the new would put the wrong list under
 * the right chip — 124's race, so the same sequence guard.
 */
export function SupportAskOne({ me }: { me: Me }) {
  const reader = can(me, 'analytics.read')
  const canDraft = can(me, 'content.draft')

  const [days, setDays] = useState<AskOneDays>(30)
  const [filter, setFilter] = useState<AskOneFilter>('action')
  const [themeId, setThemeId] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  const [summary, setSummary] = useState<AskOneSummary | null>(null)
  const [themes, setThemes] = useState<AskOneTheme[] | null>(null)
  const [rows, setRows] = useState<AskOneQuestion[] | null>(null)
  const [total, setTotal] = useState(0)
  const [counts, setCounts] = useState<Record<AskOneFilter, number> | null>(null)
  const [error, setError] = useState<string | null>(null)

  const [openKey, setOpenKey] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [actError, setActError] = useState<string | null>(null)
  const [reload, setReload] = useState(0)

  // The summary and the themes follow the PERIOD only.
  useEffect(() => {
    if (!reader) return
    let live = true
    setSummary(null)
    setThemes(null)
    loadAskOneSummary(days)
      .then((s) => live && setSummary(s))
      .catch((e) => live && setError(messageOf(e)))
    loadAskOneThemes(days)
      .then((t) => live && setThemes(t))
      .catch(() => live && setThemes([]))
    return () => {
      live = false
    }
  }, [reader, days, reload])

  // The list and its chips follow everything.
  const seq = useRef(0)
  useEffect(() => {
    if (!reader) return
    const mine = ++seq.current
    const t = window.setTimeout(() => {
      loadAskOneQuestions({ days, filter, themeId, search, limit: 50 })
        .then((p) => {
          if (mine !== seq.current) return
          setRows(p.rows)
          setTotal(p.total)
          setOpenKey((cur) => (cur && p.rows.some((r) => r.foldKey === cur) ? cur : p.rows[0]?.foldKey ?? null))
        })
        .catch((e) => mine === seq.current && setError(messageOf(e)))
      loadAskOneCounts(days, themeId)
        .then((c) => mine === seq.current && setCounts(c))
        .catch(() => undefined)
    }, search ? 250 : 0)
    return () => window.clearTimeout(t)
  }, [reader, days, filter, themeId, search, reload])

  async function act(fn: () => Promise<unknown>) {
    setBusy(true)
    setActError(null)
    try {
      await fn()
      setReload((n) => n + 1)
    } catch (e) {
      setActError(messageOf(e))
    } finally {
      setBusy(false)
    }
  }

  const open = rows?.find((r) => r.foldKey === openKey) ?? null
  const themeTitle = themeId ? themes?.find((t) => t.id === themeId)?.title ?? 'selected' : null

  const shell = (children: React.ReactNode) => (
    <AppShell
      current="Ask One"
      canAdmin={can(me, 'admin.roles')}
      canShop={canReachShopAdmin(me)}
      canModerate={can(me, 'content.moderate')}
      canStarsAdmin={can(me, 'stars.admin')}
      canPhotoRoom={canGallery(me)}
      canAnalytics={reader}
      canNotices={can(me, 'content.publish')}
      canAudit={can(me, 'audit.read')}
      supportLanes={myLanes(me)}
      userName={me.displayName}
      userInitials={initialsOf(me.displayName)}
      userRole={me.roles[0]?.name ?? 'No role'}
      locale={me.locale}
      onSignOut={() => supabase.auth.signOut()}
    >
      {children}
    </AppShell>
  )

  if (!reader) {
    return shell(
      <p className="roles__error" role="alert">
        The Ask One tab needs analytics.read. Comms admins and the tenant admin hold it; lane holders do not, by design.
      </p>,
    )
  }

  return shell(
    <>
      <SupportTabs on="ask-one" deskLabel={myLanes(me).length ? 'Desk' : 'Your questions'} />
      <div className="askone__top">
        <p className="supportinbox__lede">
          What people asked One, how it answered, and what to do about it. Nobody is named.
        </p>
        <div className="chips" role="group" aria-label="Period">
          {([30, 7] as const).map((d) => (
            <button
              key={d}
              type="button"
              className={`chip chip--sm${days === d ? ' chip--on' : ''}`}
              aria-pressed={days === d}
              onClick={() => {
                setDays(d)
                setThemeId(null)
              }}
            >
              Last {d} days
            </button>
          ))}
        </div>
      </div>
      {error && <p className="roles__error" role="alert">{error}</p>}

      {summary ? <AskOneTiles summary={summary} /> : <p className="queue__empty">Loading…</p>}

      <Card title="Conversations started, by day">
        {summary ? <AskOneChart daily={summary.daily} /> : <p className="queue__empty">Loading…</p>}
      </Card>

      <div className="askone__two">
        <Card className="askone__themecard">
          <AskOneThemes
            themes={themes}
            activeId={themeId}
            days={days}
            onPick={(id) => {
              setThemeId(id)
              setFilter(id ? 'all' : 'action')
            }}
          />
        </Card>
        <Card>{summary ? <AskOneNext summary={summary} /> : <p className="queue__empty">Loading…</p>}</Card>
      </div>

      <section className="desk askone__desk" aria-label="Questions">
        <div className="desk__queue">
          <AskOneList
            rows={rows}
            total={total}
            counts={counts}
            filter={filter}
            onFilter={setFilter}
            search={search}
            onSearch={setSearch}
            themeTitle={themeTitle}
            onClearTheme={() => {
              setThemeId(null)
              setFilter('action')
            }}
            openKey={openKey}
            onOpen={setOpenKey}
          />
        </div>
        <div className="askone__reading">
          {open ? (
            <AskOneDetail
              key={`${open.foldKey}-${open.reviewState ?? ''}-${open.trackerIssueId ?? ''}`}
              q={open}
              canDraft={canDraft}
              busy={busy}
              error={actError}
              onReview={(st, r) => void act(() => reviewAskOne(open.foldKey, st, r))}
              onReopen={() => void act(() => reopenAskOne(open.foldKey))}
              onDraft={(qq, a) => void act(() => draftWikiAnswerFromAsk(open.foldKey, qq, a))}
              onSend={(t, b) => void act(() => sendAskOneToDevelopers(open.foldKey, t, b))}
            />
          ) : (
            <p className="desk__none">{rows && rows.length === 0 ? 'Nothing to open.' : 'Pick a question.'}</p>
          )}
        </div>
      </section>
    </>,
  )
}

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('')
}
